import React, { forwardRef, useEffect, useRef, useState } from "react";
import { AssignmentOutlined, CancelOutlined, FactCheckOutlined, TaskAltOutlined } from "@mui/icons-material";
import { Alert, Box, Button, ButtonBase, Chip, Skeleton, Tooltip, Typography } from "@mui/material";
import { WorkflowMyWorkItem } from "./workflowInstancesData";
import { WorkflowViewerRoleDto } from "../../types/workflowDto";

interface WorkflowMyWorkPanelProps {
  items: WorkflowMyWorkItem[];
  viewerRole: WorkflowViewerRoleDto | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpen: (item: WorkflowMyWorkItem) => void;
}

const workKindLabel = (item: WorkflowMyWorkItem): string => {
  // Admin sees other people's open items; those are viewable, not actionable.
  if (!item.canAct) return "View only";
  if (item.kind === "approval") return "Needs signature";
  // A case parked on a cancel stage (after a reject) waits for its owner to close it.
  return item.kind === "close" ? "Close case" : "To do";
};

const WorkKindIcon: React.FC<{ kind: WorkflowMyWorkItem["kind"] }> = ({ kind }) => {
  if (kind === "approval") return <FactCheckOutlined />;
  if (kind === "form") return <AssignmentOutlined />;
  if (kind === "close") return <CancelOutlined />;
  return <TaskAltOutlined />;
};

// Same idea as the folder tree: measure the rendered line and only then offer the full text.
const EllipsisText: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      const next = el.scrollWidth > el.clientWidth + 1;
      setTruncated((current) => (current === next ? current : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip
      title={text}
      placement="bottom-start"
      disableHoverListener={!truncated}
      disableFocusListener={!truncated}
      disableTouchListener={!truncated}
    >
      <Typography ref={ref}>{text}</Typography>
    </Tooltip>
  );
};

interface WorkGroupProps {
  heading: string;
  items: WorkflowMyWorkItem[];
  onOpen: (item: WorkflowMyWorkItem) => void;
}

const WorkGroup: React.FC<WorkGroupProps> = ({ heading, items, onOpen }) => (
  <Box className="workflow-instances__work-group">
    <Typography component="h3">{heading}</Typography>
    {items.map((item) => (
      <ButtonBase key={item.id} className="workflow-instances__work-item" onClick={() => onOpen(item)}>
        <Box className="workflow-instances__work-icon">
          <WorkKindIcon kind={item.kind} />
        </Box>
        <Box className="workflow-instances__work-copy">
          <EllipsisText text={item.stageName} />
          <EllipsisText text={item.refNo ? `${item.caseTitle} · ${item.refNo}` : item.caseTitle} />
        </Box>
        {/* One grid slot for both chips, so the Test badge never pushes the work kind onto its own line. */}
        <Box className="workflow-instances__work-chips">
          {item.isTest && <Chip size="small" variant="outlined" color="secondary" label="Test" className="workflow-instances__test-chip" />}
          <Chip size="small" label={workKindLabel(item)} color={item.isOverdue && item.canAct ? "warning" : "default"} />
        </Box>
      </ButtonBase>
    ))}
  </Box>
);

/**
 * Epic 80431 - the My Work inbox panel: what is waiting on the user, as the server scoped it.
 * Loading, error and empty are this panel's own, independent of the Instances table beside it.
 * Focusable (tabIndex -1) so /workflow/my-work can land on it.
 */
const WorkflowMyWorkPanel = forwardRef<HTMLElement, WorkflowMyWorkPanelProps>(
  ({ items, viewerRole, loading, error, onRetry, onOpen }, ref) => {
    const approvals = items.filter((item) => item.kind === "approval");
    const actionableWork = items.filter((item) => item.kind !== "approval");

    return (
      <Box
        component="aside"
        ref={ref}
        tabIndex={-1}
        aria-labelledby="workflow-my-work-heading"
        className="workflow-instances__panel workflow-instances__my-work"
      >
        <Box className="workflow-instances__panel-heading">
          <Typography component="h2" id="workflow-my-work-heading">
            My Work
          </Typography>
        </Box>

        <Box className="workflow-instances__work-scroll">
          {loading ? (
            <Box aria-label="Loading My Work">
              <Skeleton height={74} />
              <Skeleton height={74} />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              }
            >
              My Work could not be loaded.
            </Alert>
          ) : items.length === 0 ? (
            <Box className="workflow-instances__empty">
              Nothing waiting on you as {viewerRole ?? "Applicant / Employee"}.
            </Box>
          ) : (
            <>
              {approvals.length > 0 && <WorkGroup heading="Approvals" items={approvals} onOpen={onOpen} />}
              {actionableWork.length > 0 && (
                <WorkGroup heading="Tasks & forms" items={actionableWork} onOpen={onOpen} />
              )}
            </>
          )}
        </Box>
      </Box>
    );
  },
);

WorkflowMyWorkPanel.displayName = "WorkflowMyWorkPanel";

export default WorkflowMyWorkPanel;
